import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from 'react-router-dom';

export default function CardScroller({
    title = "",
    buttonName = "",
    py = 5,
    pathname = "",
    id = null,
    schema = { name: "name", image: "path" },
    items = [],
    // 👇 px per second (change this prop to control speed)
    speed = 200,
    // if true, hovering the entire container pauses (including gaps). Default false: only hovering items pauses.
    pauseOnContainer = false,
    isHideButton = false
}) {
    const scrollRef = useRef(null);

    const [isPaused, setIsPaused] = useState(false);
    const isPausedRef = useRef(false);
    const speedRef = useRef(speed);
    const positionRef = useRef(0);
    const lastTimeRef = useRef(null);

    // keep ref in sync (hover/state updates won't re-run effect unnecessarily)
    useEffect(() => {
        isPausedRef.current = isPaused;
    }, [isPaused]);

    useEffect(() => {
        speedRef.current = speed;
    }, [speed]);

    // 🔁 double list for seamless loop
    const loopItems = useMemo(() => {
        if (!items || items.length === 0) return [];
        return [...items, ...items];
    }, [items]);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        let frameId;

        const step = (timestamp) => {
            if (lastTimeRef.current == null) {
                lastTimeRef.current = timestamp;
            }

            const delta = timestamp - lastTimeRef.current; // ms
            lastTimeRef.current = timestamp;

            if (
                !isPausedRef.current &&
                container.scrollWidth > container.clientWidth
            ) {
                const fullWidth = container.scrollWidth;
                const singleWidth = fullWidth / 2; // one copy width

                // px/sec  → px/frame
                const distance = (speedRef.current * delta) / 1000;

                let nextPos = positionRef.current + distance;

                // wrap for infinite loop
                if (nextPos >= singleWidth) {
                    nextPos -= singleWidth;
                }

                positionRef.current = nextPos;
                container.scrollLeft = nextPos;
            }

            frameId = requestAnimationFrame(step);
        };

        frameId = requestAnimationFrame(step);

        return () => {
            cancelAnimationFrame(frameId);
            lastTimeRef.current = null;
        };
    }, [loopItems.length]);

    // handlers to pause when hovering an item
    const handleItemPointerEnter = () => {
        isPausedRef.current = true;
        setIsPaused(true);
    };
    const handleItemPointerLeave = () => {
        isPausedRef.current = false;
        setIsPaused(false);
    };

    // optional: pause when hovering container (including gaps)
    const handleContainerPointerEnter = () => {
        if (!pauseOnContainer) return;
        isPausedRef.current = true;
        setIsPaused(true);
    };
    const handleContainerPointerLeave = () => {
        if (!pauseOnContainer) return;
        isPausedRef.current = false;
        setIsPaused(false);
    };

    return (
        <section className={`w-full bg-gradient-radial py-5 lg:py-${py}`} id={id}>
            <div className="container mx-auto px-4 lg:px-15">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8 lg:mb-12">
                    <h2 className="text-nowrap text-2xl sm:text-3xl lg:text-5xl font-bold font-urbanist text-white text-center sm:text-left">
                        {title || "Our Partners"}
                    </h2>
                    {
                        !isHideButton &&
                        <Link to={pathname || '#'} className="text-nowrap px-4 md:px-8 py-1.5 md:py-3 bg-white rounded-[50px] text-neutral-950 text-sm sm:text-md lg:text-lg font-semibold font-inter hover:!text-neutral-950 hover:shadow-[0px_0px_5px_5px_rgba(171,198,248,0.5)] focus:!text-neutral-950 transition-shadow">
                            {buttonName || "Read More"}
                        </Link>
                    }
                </div>

                <div
                    ref={scrollRef}
                    className="flex overflow-x-hidden space-x-12 pb-4 md:pb-0"
                    style={{ willChange: "scroll-position" }}
                    onPointerEnter={handleContainerPointerEnter}
                    onPointerLeave={handleContainerPointerLeave}
                >
                    {loopItems.map((pointDetails, index) => {
                        const name = pointDetails[schema?.name || "name"];
                        const image = pointDetails[schema?.image || "image"];

                        return (
                            <div
                                key={`${index}_${name}`}
                                onPointerEnter={handleItemPointerEnter}
                                onPointerLeave={handleItemPointerLeave}
                                className="flex-shrink-0 w-[200px] h-[200px] md:w-[250px] md:h-[250px] bg-white rounded-full flex items-center justify-center  p-1 sm:p-5 md:p-5 lg:p-6 shadow-lg overflow-hidden"
                            >
                                <img
                                    src={image}
                                    alt={(name || "").replace(/ /g, "-")}
                                    className="max-w-[100%] max-h-[100%] object-contain"
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
